




// Vue
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Mixins } from "vue-mixin-decorator";
import { mapGetters, mapActions } from "vuex";

// Third party
import dayjs from "dayjs";
import "dayjs/locale/es";

// Filters
import formatPhone from "@/shared/filters/formatPhone.filter.ts";
import formatDate from "@/shared/filters/formatDate.filter.ts";

// Constants
import Assets from "@/constants/assets.ts";
import Endpoints from "@/constants/endpoints";
import Constants from "@/constants/constants";
import RouteNames from '@/constants/route-names';

// Components
import ExpansionPanel from "@/shared/components/expansion.panel.component.vue";
import Icon, { IconEnum } from "@/shared/components/icon.component.vue";
import { Participant } from "@/manager/convocation/participant.model";
import Input, {
  InputType,
  InputStyle,
  InputLabelColor,
} from "@/shared/components/input.component.vue";
import Btn, {
  BtnColor,
  BtnWidth,
  BtnPredefinedStyle,
} from "@/shared/components/btn.component.vue";
import Tooltip, {
  TooltipColor,
} from "@/shared/components/Tooltip.component.vue";
import CheckIcon, {
  CheckIconColor,
} from "@/shared/components/CheckIcon.component.vue";
import FlatSelectorText, {
  FlatSelectorTextBorderBottom,
} from "@/shared/components/FlatSelectorText.component.vue";
import FlatSelectorTextMultiple, {
  FlatSelectorTextMultipleBorderBottom,
} from "@/shared/components/FlatSelectorTextMultiple.component.vue";
import FlatSelectorIcon from "@/shared/components/FlatSelectorIcon/FlatSelectorIcon.component.vue";
import TextArea from "@/shared/components/text-area.component.vue";
import Modal, {
  ModalTitleColor,
} from "@/shared/components/Modal.component.vue";
import Spinner from "@/shared/components/Spinner.component.vue";
import Field from "@/shared/components/field.component.vue";
import TwoLevelsSelector from "@/shared/components/twoLevelsSelector/TwoLevelsSelector.component.vue";
import NoteItem from "@/manager/convocation/views/convocationDetail/noteItem/noteItem.view.vue";
import SpotlightText, {
  SpotlightTextColor,
} from "@/shared/components/SpotlightText.component.vue";
import PurchaseParticipantTable from "@/manager/purchase/views/convocation-detail/purchase-row/purchase-participant-table/index.vue";
import SelectTag from "@/shared/components/select.tag.component.vue";
import ButtonSwitch, {
  SwitchActiveColor,
  ButtonSwitchPredefinedStyle,
} from "@/shared/components/button-switch.component.vue";
import SearchAutocomplete from '@/shared/components/SearchAutocomplete.component.vue';

// Models
import Bill, { TransactionType } from '@/manager/billing/models/bill.model';
import { Note } from "@/manager/convocation/note.model";
import { SecondLevelOptionModel } from "@/shared/models/secondLevelOption.model";
import {
  PurchasePaymentStatus,
  PurchaseCompletionStatus,
  PaymentMethod,
} from "@/clickbuy/purchase/models/purchase.model";
import { TypeOfProduct } from "@/manager/product/models/product.model.ts";

// Mixins
import WithSpinner from "@/shared/mixins/withSpinner.mixin.ts";

// Services
import { ConvocationService } from "@/manager/convocation/convocation.service";

import FloatMenu, {
  FloatMenuPosition,
} from "@/shared/components/float-menu.component.vue";

import {
  BudgetPaymentStatus,
  BudgetPaymentStatusLiteral,
  BudgetCommunicationStatus,
  BudgetCommunicationStatusListeral,
  BudgetAlert,
  BudgetAlertLiteral,
} from "@/manager/budget/models/budget.model";

import MenuElement, {
  MenuElementBgColor,
  MenuElementBgColorHover,
  MenuElementSize,
} from "@/shared/components/menu-element.component.vue";

import FlatSelectorSwitch from "@/shared/components/flat-selector-switch.component.vue";

// Services
import { PurchaseService } from '@/manager/purchase/purchase.service';
import BillingService from '@/manager/billing/billing.service';
import BudgetService from "@/manager/budget/budget.service";

enum IconBudgetColor {
  Red = "color-red",
  BlueDark = "color-blue-dark",
}

@Component({
  components: {
    Btn,
    ButtonSwitch,
    Icon,
    Input,
    CheckIcon,
    ExpansionPanel,
    Field,
    FlatSelectorIcon,
    FlatSelectorText,
    FlatSelectorTextMultiple,
    TextArea,
    Tooltip,
    Modal,
    TwoLevelsSelector,
    Spinner,
    NoteItem,
    SpotlightText,
    PurchaseParticipantTable,
    SelectTag,
    MenuElement,
    FloatMenu,
    FlatSelectorSwitch,
    SearchAutocomplete,
  },
  methods: {
    ...mapActions({
      fetchProductList: 'fetchProductList',
      setCurrentBill: 'setCurrentBill',
    }),
  },
  computed: {
    ...mapGetters({
      getProduct: 'getProduct',
      productList: 'getProductList',
      getProfile: "getProfile",
      getPurchase: "getPurchase",
      getProcedencyList: "procedencyList",
      getPurchaseNotes: "getPurchaseNotes",
      getBudgetNotes: "getBudgetNotes",
      getConvocation: "getConvocation",
      clientList: "getClientList"
    }),
  },
  filters: {
    formatDate,
    formatPhone,
  },
})
export default class PurchaseRow extends Mixins<WithSpinner>(WithSpinner) {
  @Prop() private rowNumber!: number;
  @Prop() private purchaseId!: string;
  @Prop() private convocationId!: string;
  @Prop() private columnsVisibility!: any;
  @Prop() private documentType!: any;
  @Prop({ default: false }) private special!: boolean;

  private assets = Assets;
  private openCollapsableInfo = false;
  private showPurchaseTrace = false;
  private yetOpenedCollapsableInfo = false;
  private iconEnum = IconEnum;
  private inputType = InputType;
  private inputStyle = InputStyle;
  private inputLabelColor = InputLabelColor;
  private checkIconColor = CheckIconColor;
  private tooltipColor = TooltipColor;
  private btnWidth = BtnWidth;
  private btnColor = BtnColor;
  private btnPredefinedClass = BtnPredefinedStyle;
  private modalTitleColor = ModalTitleColor;
  private getProfile!: any;
  private getPurchase!: any;
  private getPurchaseNotes!: any;
  private getBudgetNotes!: any;
  private getProcedencyList!: any;
  private getConvocation!: any;
  private contentWithEllipsis = {
    firstName: false,
    lastName: false,
  };
  private spotlightTextColor = SpotlightTextColor;
  private deletePurchaseVisibility = false;
  private annullatePurchaseVisibility = false;
  private deletePurchaseModalOpened = false;
  private addObservationPurchaseModalOpened = false;
  private modalNoteEmpty = false;
  private modalNote = "";

  private floatMenuPosition = FloatMenuPosition;
  private menuElementSize = MenuElementSize;
  private menuElementBgColor = MenuElementBgColor;
  private menuElementBgColorHover = MenuElementBgColorHover;
  private constants = Constants;
  private timeSwitchSelected = this.constants.switchGenerals;
  private getProduct!: any;
  private productList!: any;
  private fetchProductList!: any;
  private convocationParticipant!: any
  private cambioDatosModalOpened = false;
  private setCurrentBill!: any;
  private BillhasRefundedBill = false;
  private refundedOriginBillExist = false
  private colValue!: any;
  private searchClient: any = false;
  private mentions: any = [];
  private selectedClient!: any;
  private searchString = "";
  private searchTimeout: any = null

  @Watch("modalNote")
  private watchModalNote(newVal: any, oldVal: any) {
    if (newVal.slice(-1) == "$") {
      this.searchClient = true;
    } else {
      this.searchClient = false;
    }
  }

  @Watch("searchString")
  public watchsearchString(newVal: any, oldVal: any) {
    if (newVal && newVal.length > 3 && !this.selectedClient) {
      if (this.searchTimeout != null) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.searchCallback();
      }, 1000);
    }
  }

  public searchCallback() {
    if (this.searchString === "") {
      this.selectedClient = null;
    }
    this.fetchClientList();
  }

  private clearClient() {
    this.selectedClient = null;
  }

  private async selectClient(option: any) {
    this.selectedClient = option;
    this.searchClient = false;
    // console.log(this.selectedClient);
    this.modalNote += this.selectedClient.text;
    this.mentions.push(this.selectedClient)
  }


  private created() {
    this.$store.dispatch("fetchProcedency");
    this.fetchProductList({ limit: 1000 })
    this.fetchConvocationFromBudget()
    this.fetchConvocationFromPurchase()
    this.spinner.minimumTimeToShow = 500;
    this.checkContentWithEllipsis();
    if (this.purchase.bill) {
      this.fetchRefundedOriginBill(this.purchase.bill.id)
    }
  }

  private fetchClientList() {
    const payload = {
      limit: 20,
      search: this.searchString
    };
    this.$store.dispatch("fetchClientList", payload);
  }

  private fetchRefundedOriginBill(billId: any) {
    this.$store.dispatch('fetchRefundedOriginBill', { id: billId }).then(
      res => {
        if (res.originBill) {
          this.checkRefundedOriginBillExist(true);
        } else if (res.refundedBill) {
          this.checkBillhasRefundedBill(true);
        }
      }
    );
  }

  private checkRefundedOriginBillExist(newVal: any) {
    if (newVal != null) {
      this.refundedOriginBillExist = true;
    } else {
      this.refundedOriginBillExist = false;
    }
  }

  private checkBillhasRefundedBill(newVal: any) {
    if (newVal != null) {
      this.BillhasRefundedBill = true;
    } else {
      this.BillhasRefundedBill = false;
    }
  }

  private mounted() {
    window.addEventListener('resize', this.handleResize);
    window.addEventListener("resize", this.checkContentWithEllipsis);
  }

  private beforeDestroy() {
    // Remove the resize event listener when the component is destroyed
    window.removeEventListener('resize', this.handleResize);
  }

  private colspanValue() {
    // Dynamically determine colspan based on the display width
    this.colValue = this.$el.clientWidth <= 1500 ? 12 : 6;
  }

  private handleResize() {
    this.colspanValue()
    this.$forceUpdate(); // Force update to recalculate colspan on resize
  }

  get convocation() {
    return this.getConvocation(this.convocationId);
  }

  // get convocationDate(){
  //   return formatDate(this.convocationParticipant.timetables[0].date, "DD MMM YY")
  // }

  private convocationDate(convocation: any) {
    return formatDate(convocation.timetables[0].date, "DD MMM YY")
  }

  get isConference() {
    return this.convocation.product.typeOfProduct === TypeOfProduct.Conference;
  }

  get product() {
    if (this.purchase.purchaseLines.length > 0) {
      return this.getProduct(this.purchase.purchaseLines[0].product);
    } else {
      return {
        'acronym': '',
        'name': 'Sin producto asociado'
      }
    }
  }

  get clientProcedency() {
    let result = "-";
    if (this.purchase.client.procedency) {
      const procedency = this.getProcedencyList.find((procedency: any) => {
        return procedency.id === this.purchase.client.procedency;
      });
      if (procedency) {
        result = procedency.name;
      }
    }
    return result;
  }

  get clientTag() {
    try {
      return this.purchase.client.tag.map((e: any) => {
        return { ...e, active: true };
      });
    } catch (_) {
      return [];
    }
  }

  get purchaseTags() {
    try {
      return (this.purchase.tags || []).map((e: any) => {
        return { ...e, active: true };
      });
    } catch (_) {
      return [];
    }
  }

  get employeeInfo() {
    const profile = this.purchase.employee.user.profile;
    const fullName = this.purchase.employee.user.fullName;
    const result = {
      avatar: Assets.defaultAvatar,
      name: fullName,
    };
    if (profile !== null && profile.avatar !== null) {
      result.avatar = profile.avatar;
    }
    if (!fullName) {
      result.name = "Empleado sin nombre";
    }

    return result;
  }

  get firstColumn() {
    try {
      let date;
      if (this.purchase.bill) {
        date = this.purchase.bill.date;
      } else {
        date = this.purchase.created;
      }
      const format = "D/MM/YYYY";
      date = (this.$options as any).filters.formatDate(date, format);
      // return this.special ? date : this.purchase.publicId;
      return date;

    } catch (_) {
      return this.purchase.publicId;
    }
  }

  get purchase() {
    return this.getPurchase(this.purchaseId);
  }

  get oddRow() {
    return this.rowNumber % 2 !== 0;
  }

  get notes() {
    return this.getPurchaseNotes(this.purchaseId);
  }

  get purchaseTotalPrice() {
    const purchaseDate = new Date(this.purchase.created);
    const referenceDate = new Date(2024, 4, 3, 15, 30);

    if (purchaseDate < referenceDate) {
      return this.purchase.totalPrice + this.purchase.totalPaymentPending;
    } else {
      return this.purchase.totalPrice;
    }
  }


  get showSpinnerNoteRow() {
    return this.openCollapsableInfo && this.spinner.show;
  }

  get modalNotePlaceholder() {
    let placeholder =
      "Comparte alguna nota con tus compañeros sobre esta compra...";
    if (this.modalNoteEmpty) {
      placeholder = "Este campo no puede estar vacío...";
    }
    return placeholder;
  }

  get noteListTransitionName() {
    let transitionName = "note-list";
    if (!this.yetOpenedCollapsableInfo) {
      transitionName += "-disabled";
    }
    return transitionName;
  }

  get observationsTooltip() {
    return "1 observación";
  }

  get purchaseDocument() {
    return this.purchase.document;
  }

  get purchaseBillDocument() {
    return this.purchase.bill.document;
  }

  private togglePurchaseObservationModalOpened(value: boolean) {
    this.addObservationPurchaseModalOpened = value;
    if (!value) {
      setTimeout(() => {
        this.mentions = [];
      }, 500);
      this.searchClient = false;
    }
  }

  get purchaseProduct() {
    try {
      let text = this.purchase.purchaseLines[0].productName;
      const lines = this.purchase.purchaseLines.length;
      if (lines > 1) {
        text = `${lines} productos`;
      }
      return text;
    } catch (_) {
      return "...";
    }
  }

  get hasPurchaseDocument() {
    return this.purchase.document !== "" && this.purchase.document !== null;
  }

  get hasBillDocument() {
    return (
      this.purchase.bill !== null &&
      this.purchase.bill.document !== "" &&
      this.purchase.bill.document !== null
    );
  }

  get hasBudget() {
    return false;
  }

  get paymentMethod() {
    let method = {
      text: "Efectivo",
      icon: IconEnum.MenuSync,
    };

    if (this.purchase.paymentMethod === PaymentMethod.Card) {
      method = {
        text: "Tarjeta",
        icon: IconEnum.CreditCard,
      };
    } else if (this.purchase.paymentMethod === PaymentMethod.SendWireTransfer) {
      method = {
        text: "Transferencia",
        icon: IconEnum.Bank,
      };
    } else if (this.purchase.paymentMethod === PaymentMethod.Bizum) {
      method = {
        text: "Bizum",
        icon: IconEnum.Phone,
      };
    }

    return method;
  }

  get billIsEmitted() {
    if (this.purchase.bill) {
      return (this.purchase.bill.transactionType === TransactionType.TransactionEmitted);
    }
  }

  get timeFilter(): any[] {
    return [
      this.constants.switchGenerals,
      this.constants.switchConvocations,
      this.constants.switchPurchases,
      this.constants.switchBudgets,
      this.constants.switchBills,
      this.constants.switchFiscalDataExtension,
      this.constants.switchPaymentMethods,
    ];
  }

  get clientProfile() {
    if (this.purchase.client && this.purchase.client.user && this.purchase.client.user.id) {
      const route = {
        name: RouteNames.ClientEdit,
        params: {
          id: this.purchase.client.user.id,
        },
      };
      return route;
    }
  }

  get convocationBackUrl() {
    let url = ""
    if (this.convocation) {
      url = window.location.origin + "/api/admin/convocation/convocation/" + this.convocation.id + "/change"
    }
    return url
  }

  get budgetBackUrl() {
    let url = ""
    if (this.purchase.budget && this.purchase.budget) {
      url = window.location.origin + "/api/admin/purchase/budget/" + this.purchase.budget.id + "/change"
    }
    return url
  }

  get routerLinkToBudget() {
    let route: any
    if (this.purchase.budget) {
      route = {
        name: RouteNames.BudgetName,
        params: {
          id: this.purchase.budget.id,
        },
      };
    }
    return route;
  }

  get routerLinkToBill() {
    let route: any
    if (this.purchase.bill) {
      route = {
        name: RouteNames.BillList,
        params: {
          id: this.purchase.bill.id,
        },
        query: {
          "search-string": this.purchase.bill.displayId,
        },
      };
    }
    return route;
  }

  private routerLinkToConvocation(convocationId: any) {
    const route = {
      name: RouteNames.ConvocationDetail,
      params: {
        // id: this.convocationParticipant.id,
        id: convocationId,
      },
      query: {
        "search-string": this.eliminarAcentos(this.purchase.client.user.fullName),
      },
    };
    return route;
  }

  private modifyDataPrivate() {
    this.cambioDatosModal(false)
    this.$emit('modify-data');
    this.editBill();
  }

  private cambioDatosModal(value: boolean) {
    this.cambioDatosModalOpened = value;
  }

  private editBill() {
    const billToEdit = Object.assign({}, this.purchase.bill);
    billToEdit.billLine = Object.assign([], this.purchase.bill.billLine);
    this.setCurrentBill(billToEdit);
    this.$emit('edit-bill');
    this.$emit('purchase-bill', billToEdit)
  }

  private eliminarAcentos(texto: string) {
    return texto.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  }

  get purchaseBackUrl() {
    const url = window.location.origin + "/api/admin/purchase/purchase/" + this.purchase.id + "/change"
    return url
  }

  private purchaseLineBackUrl(purchaseLine: any) {
    const url = window.location.origin + "/api/admin/purchase/purchaseline/" + purchaseLine.id + "/change"
    return url
  }

  private emitMessageError(response: any) {
    if (response.data && response.data.detail) {
      this.$emit("show-message", response.data.detail);
    }
  }

  private checkContentWithEllipsis() {
    setTimeout(() => {
      const firstNameColumnElement = this.$refs
        .refFirstNameColumn as HTMLElement;
      this.contentWithEllipsis.firstName =
        firstNameColumnElement &&
        firstNameColumnElement.scrollWidth > firstNameColumnElement.clientWidth;
      const lastNameColumnElement = this.$refs.refLastNameColumn as HTMLElement;
      this.contentWithEllipsis.lastName =
        lastNameColumnElement &&
        lastNameColumnElement.scrollWidth > lastNameColumnElement.clientWidth;
    }, 0);
  }

  get showNoteRow() {
    return this.openCollapsableInfo && !this.spinner.show;
  }

  private toggleCollapsableInfo() {
    this.handleResize()
    this.openCollapsableInfo = !this.openCollapsableInfo;
    if (this.openCollapsableInfo) {
      Vue.nextTick(() => {
        this.fetchPurchaseNotes();
      });
    }
  }

  private toggleCollapsableTrace() {
    this.showPurchaseTrace = !this.showPurchaseTrace;
  }

  private resetModalNoteEmpty(force: boolean) {
    if (force || this.modalNote === "") {
      this.modalNoteEmpty = false;
      this.modalNote = "";
    }
  }

  private colspanNumber(colspan: number) {
    let finalNumber = colspan;
    if (!this.columnsVisibility.observations) {
      finalNumber--;
    }
    return finalNumber;
  }

  private closeDeletePurchaseModal() {
    this.deletePurchaseVisibility = false;
  }

  private closeAnnullatePurchaseModal() {
    this.annullatePurchaseVisibility = false;
  }

  private sendDocumentEmail(id: any) {
    PurchaseService.sendPurchaseDocument(id, {})
      .then((res) => { this.$emit("show-message", res.data.details) })
  }

  private sendDocumentBillEmail(bill: any) {
    if (bill.id) {
      BillingService.sendBillEmail(bill.id)
        .then((res) => { this.$emit("show-message", res.data.details) })
    }
  }

  private seeDocument(url: any) {
    window.open(url, '_blank');
  }

  private showAnnullatePurchaseModal() {
    this.annullatePurchaseVisibility = true;
  }

  private showdeletePurchaseModal() {
    this.deletePurchaseVisibility = true;
  }

  private refetchList() {
    this.$emit("refetch-list");
  }

  private annullatePurchase() {
    this.annullatePurchaseVisibility = false;
    this.$store.dispatch("annullatePurchase", { id: this.purchaseId, purchaseStatus: 0 }).then(() => {
      this.refetchList();
    });
  }

  private deletePurchase() {
    this.$store.dispatch("deletePurchase", { id: this.purchaseId }).then(() => {
      this.closeDeletePurchaseModal();
      this.refetchList();
    });
  }

  private fetchPurchaseNotes() {
    this.requestShowSpinner();
    this.$store
      .dispatch("fetchPurchaseNotes", { id: this.purchaseId })
      // .dispatch("fetchBudgetNotes", { id: '69b9b1c9-54dd-475f-98ee-634692ceaf8b' })
      .then((res) => {
        this.changeSpinnerVisibility(false);
        if (!this.yetOpenedCollapsableInfo) {
          this.yetOpenedCollapsableInfo = true;
        }
      });
  }

  private fetchConvocationFromBudget() {
    if (this.purchase.convocation) {
      this.convocationParticipant = this.purchase.convocation
    } else if (this.purchase.budget && this.purchase.budget.id) {
      this.$store
        .dispatch("fetchConvocationFromBudget", { id: this.purchase.budget.id })
        // .dispatch("fetchBudgetNotes", { id: '69b9b1c9-54dd-475f-98ee-634692ceaf8b' })
        .then((res) => {
          this.convocationParticipant = res
        })
        .catch((err) => {
        });
    }
  }

  private fetchConvocationFromPurchase() {
    if (this.purchase.convocation) {
      this.convocationParticipant = this.purchase.convocation
    } else if (this.purchase && this.purchase.id) {
      this.$store
        .dispatch("fetchConvocationFromPurchase", { id: this.purchase.id })
        // .dispatch("fetchBudgetNotes", { id: '69b9b1c9-54dd-475f-98ee-634692ceaf8b' })
        .then((res) => {
          this.convocationParticipant = res
        })
        .catch((err) => {
        });
    }
  }

  private generateUrlChangeData() {
    var changeDataUrl = (window.location.href.split('app')[0] + 'public/billing/' + this.purchase.bill.id);
    const navigatorAny:any = navigator
    navigatorAny.clipboard.writeText(changeDataUrl)
      .then(() => {
        this.emitMessageError({ 'data': { 'detail': "Enlace copiado al portapapeles" } });
      })
      .catch((err:any) => {
        console.error('Error al copiar el enlace:', err);
      });
  }

  private addNote() {
    if (this.modalNote.length === 0) {
      this.modalNoteEmpty = true;
    } else {
      this.addObservationPurchaseModalOpened = false;
      this.requestShowSpinner();
      this.$store
        .dispatch("newPurchaseNote", {
          purchaseId: this.purchaseId,
          employee: { user: { id: this.getProfile.id } },
          note: this.modalNote,
          mentions: this.mentions,
        })
        .then((res) => {
          this.refreshNotes();
          this.mentions = [];
          this.searchString =""
          this.searchClient = false
          this.clearClient()
          this.resetModalNoteEmpty(true);
          this.fetchPurchaseNotes();
          this.changeSpinnerVisibility(false);
          // this.refetchPurchase();
        });
    }
  }

  private updateNote(noteId: string, noteContent: string, isPostponement?: string, mentions?: any[]) {
    const payload = {
      purchaseId: this.purchaseId,
      noteId,
      data: { note: noteContent, employeeModified: this.getProfile.id, isPostponement: isPostponement, mentions: mentions },
    };
    this.$store.dispatch("patchPurchaseNote", payload).then((res: any) => {
      this.refreshNotes();
      this.mentions = [];
      this.searchString =""
      this.searchClient = false
      this.clearClient()
    });
  }

  private deleteNote(noteId: string) {
    const payload = {
      id: noteId,
      noteId,
    };
    this.$store.dispatch("removePurchaseNote", payload).then((res: any) => {
      this.refetchPurchase();
    });
  }

  private refreshNotes() {
    this.requestShowSpinner();
    this.resetModalNoteEmpty(true);
    this.fetchPurchaseNotes();
    this.changeSpinnerVisibility(false);
    // this.refetchPurchase();
  }

  private refetchPurchase() {
    this.$store.dispatch("fetchPurchase", { id: this.purchaseId });
  }

  private setRequestReturn(status: any) {
    this.purchase.requestReturn = status;
    let data = { 'request_return': status };
    this.patchPurchase(data, this.purchaseId);
  }

  private setEnrolled(status: any) {
    this.purchase.enrolled = status;
    this.purchase.activated = new Date().toISOString();
    this.purchase.activatedBy = this.getProfile.id;
    let data = { 'enrolled': status, 'activated': this.purchase.activated, 'activatedBy': this.purchase.activatedBy };
    this.patchPurchase(data, this.purchaseId);
  }

  private isDisabledPurchase(purchase: any) {
    return purchase.requestReturn != 1 || purchase.purchaseStatus == 0;
  }

  private patchPurchase(data: any, id: any) {
    const payload = {
      id,
      data,
    };
    this.$store.dispatch('patchPurchase', payload).then((res:any)=>{
      this.$emit("refresh-counters");
    });
  }

  get isOldTLP() {
    return this.purchase.client.oldTopLevelPack;
  }

  get topLevelPackIconColor() {
    let color = this.checkIconColor.Gold;
    if (this.isOldTLP) {
      color = this.checkIconColor.GoldDark;
    }
    if (this.purchase.client.tlp) {
      color = this.purchase.client.tlp.color;
    }
    return color;
  }

  get topLevelPackTooltipColor() {
    // let color = this.tooltipColor.Gold;
    // if (this.isOldTLP) {
    //   color = this.tooltipColor.GoldDark;
    // }
    let color = this.tooltipColor.Secondary;
    // if (this.purchase.client.tlp) {
    //   color = this.purchase.client.tlp.color;
    // }
    return color;
  }

  get tooltipTextTLP() {
    let text = 'Top Level Pack';
    if (this.isOldTLP) {
      text = 'TLP Antiguo';
    }
    return text;
  }

  get powerPackIconColor() {
    let color = this.checkIconColor.Gold;
    if (this.purchase.client.pwp) {
      color = this.purchase.client.pwp.color;
    }
    return color;
  }

  get powerPackTooltipColor() {
    // let color = this.tooltipColor.Gold;
    let color = this.tooltipColor.Secondary;
    // if (this.purchase.client.pwp) {
    //   color = this.purchase.client.pwp.color;
    // }
    return color;
  }

  get tooltipTextPWP() {
    let text = 'Power Pack';
    return text;
  }

  get tooltipTextCN3() {
    let text = 'Certificado de nivel 3';
    return text;
  }

  get budgetStateTooltip() {
    const tooltip = {
      color: MenuElementBgColor.Secondary,
      text: BudgetCommunicationStatusListeral[this.purchase.budget.communicationStatus],
    };
    if (this.budgetIsAccepted) {
      tooltip.color = MenuElementBgColor.Primary;
    } else if (this.budgetIsRejected) {
      tooltip.color = MenuElementBgColor.Red;
    } else if (this.budgetIsDispatched) {
      tooltip.color = MenuElementBgColor.Secondary;
    }
    return tooltip;
  }

  get budgetIsDispatched() {
    return (
      this.purchase.budget.communicationStatus === BudgetCommunicationStatus.Dispatched
    );
  }

  get budgetIsAdjust() {
    return this.purchase.budget.communicationStatus === BudgetCommunicationStatus.Adjust;
  }

  get budgetIsAccepted() {
    return (
      this.purchase.budget.communicationStatus === BudgetCommunicationStatus.Accepted
    );
  }

  get budgetIsRejected() {
    return (
      this.purchase.budget.communicationStatus === BudgetCommunicationStatus.Rejected
    );
  }

  get flatSelectorCommunicationOptions() {
    const options = [
      {
        text: BudgetCommunicationStatusListeral[
          BudgetCommunicationStatus.Rejected
        ],
        color: TooltipColor.Red,
        iconColorClass: IconBudgetColor.Red,
        communicationStatus: BudgetCommunicationStatus.Rejected,
        condition: !this.budgetIsRejected,
      },
      {
        text: BudgetCommunicationStatusListeral[
          BudgetCommunicationStatus.Adjust
        ],
        color: TooltipColor.BlueDark,
        iconColorClass: IconBudgetColor.BlueDark,
        communicationStatus: BudgetCommunicationStatus.Adjust,
        condition: this.budgetIsAccepted,
      },
    ];
    return options;
  }

  get budgetTooltipActionText() {
    let text = "Enviar aceptación";
    if (this.budgetIsAdjust) {
      text = "Enviar validación";
    }
    return text;
  }

  private sendAcceptanceEmail() {
    BudgetService.sendAcceptanceEmail(this.purchase.budget.id).then((res) => {
      this.purchase.budget.communicationStatus = BudgetCommunicationStatus.Dispatched;
    });
  }

  private sendValidationEmail() {
    BudgetService.sendValidationEmail(this.purchase.budget.id).then(
    );
  }

  private sendBudgetEmail() {
    if (this.budgetIsAdjust) {
      this.sendValidationEmail();
    } else {
      this.sendAcceptanceEmail();
    }
  }

  private patchBudget(data: any) {
    const payload = {
      id: this.purchase.budget.id,
      data,
    };
    this.$store.dispatch("patchBudget", payload);
  }

  private timeFilterChanged(option: any) {
    const urlParams = new URLSearchParams();
    urlParams.append('search', option.requestName); // Añade el parámetro adicional

    const url = `${this.$router.resolve(this.clientProfile as any).href}?${urlParams.toString()}`;
    window.open(url, "_blank")
  }

  private generateUrlChangeBudgetData() {
    var changeDataUrl = (window.location.href.split('#')[0] + '#/public/budget/modify/' + this.purchase.budget.id);
    this.cambioDatosModal(false);
    const navigatorAny:any = navigator
    navigatorAny.clipboard.writeText(changeDataUrl)
      .then(() => {
        this.emitMessageError({ 'data': { 'detail': "Enlace copiado al portapapeles" } });
      })
      .catch((err:any) => {
        console.error('Error al copiar el enlace:', err);
      });
  }

}
